/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positive */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useClub } from "@/contexts/ClubContext";
import { Clock, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatCountdown(diff) {
  if (diff <= 0) return "00:00:00";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (d > 0) return `${d}d ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function CountdownBar() {
  const [countdownEvents, setCountdownEvents] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [now, setNow] = useState(Date.now());
  const { getClubAccent, clubs, setActiveClub } = useClub();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCountdowns = () => {
      axios.get(`${API}/events/countdown`).then(r => setCountdownEvents(r.data)).catch(() => {});
    };
    fetchCountdowns();
    const interval = setInterval(fetchCountdowns, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const activeEvents = useMemo(() => {
    return countdownEvents.filter(e => {
      const eventTime = new Date(e.event_timestamp).getTime();
      return eventTime - now > 0;
    });
  }, [countdownEvents, now]);

  if (dismissed || activeEvents.length === 0) return null;

  const tickerItems = activeEvents.map(event => {
    const eventTime = new Date(event.event_timestamp).getTime();
    const diff = eventTime - now;
    const clubName = clubs.find(c => c.id === event.club_id)?.name || event.club_id;
    const accent = getClubAccent(event.club_id);
    const dateStr = new Date(event.event_timestamp).toLocaleDateString("de-AT", { day: "2-digit", month: "short" });
    return { ...event, diff, clubName, accent, dateStr, countdown: formatCountdown(diff) };
  });

  // Duplicate for seamless loop
  const tickerContent = [...tickerItems, ...tickerItems, ...tickerItems];
  const animDuration = Math.max(tickerItems.length * 12, 20);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50" data-testid="countdown-bar"
      style={{ backgroundColor: "#09090bF0", borderTop: "2px solid #fbbf24" }}>
      <div className="relative overflow-hidden h-10">
        {/* Dismiss button */}
        <button onClick={() => setDismissed(true)}
          className="absolute right-0 top-0 bottom-0 z-10 px-3 flex items-center bg-gradient-to-l from-[#09090b] via-[#09090b] to-transparent"
          data-testid="countdown-dismiss">
          <X className="w-3.5 h-3.5 text-white/40 hover:text-white/80" />
        </button>

        {/* Scrolling ticker */}
        <div className="ticker-scroll flex items-center h-full whitespace-nowrap"
          style={{ animationDuration: `${animDuration}s` }}>
          {tickerContent.map((item, i) => (
            <span key={`${item.id}-${i}`}
              className="inline-flex items-center gap-2 px-5 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => { setActiveClub(item.club_id); navigate(`/kalender?event=${item.id}&t=${Date.now()}`); }}
              data-testid={`ticker-item-${item.id}`}>
              <Clock className="w-3.5 h-3.5 shrink-0 animate-pulse" style={{ color: item.accent }} />
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: item.accent }}>
                {item.clubName}
              </span>
              <span className="text-xs text-white/90">{item.title}</span>
              <span className="text-xs text-white/50">{item.dateStr} {item.time} Uhr</span>
              <span className="font-mono text-xs font-bold text-white tabular-nums">{item.countdown}</span>
              <span className="text-white/20 mx-2">|</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
